import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import styles from '../../styles/OnboardingTour.module.css';

const BASE_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to LOTW',
    body: 'This is a private Lock of the Week league. Each week you pick one game — your lock — and live or die with the result.',
    route: '/'
  },
  {
    id: 'picks',
    title: 'Weekly Picks is where you lock',
    body: 'Open Weekly Picks to see this week’s slate. That’s the only place you submit your lock.',
    route: '/weekly-picks',
    highlight: 'weekly-picks'
  },
  {
    id: 'how-to-lock',
    title: 'How to make a lock',
    body: 'Choose a side or total, confirm the line, and lock it. That’s your pick for the week unless league rules let you change it.',
    route: '/weekly-picks'
  },
  {
    id: 'stats',
    title: 'League Stats',
    body: 'Standings, lock overview, and live game status live under League Stats. Keep this open on Saturdays.',
    route: '/leaderboard',
    highlight: 'league-stats'
  }
];

const DISCORD_STEP = {
  id: 'discord',
  title: 'Get on Discord',
  body: 'Lock chatter, pick reminders, and the weekly recap happen on Discord. Create an account if you don’t have one, then join the LOTW server so you don’t miss a lock window.',
  route: '/user-settings'
};

const SETTINGS_STEP = {
  id: 'settings',
  title: 'You’re in',
  body: 'Profile, Discord verification, and SMS opt-in are under the gear icon. You can replay this tour anytime from User Settings.',
  route: '/',
  highlight: 'settings'
};

function hasDiscord(user) {
  return Boolean(user?.discord_username || user?.discord_verified_at);
}

const OnboardingTour = () => {
  const { user, mergeUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [index, setIndex] = useState(0);
  const [inviteUrl, setInviteUrl] = useState(null);

  const active = user?.onboarding_tour_status === 'pending'
    || user?.onboarding_tour_status === 'deferred';

  const steps = useMemo(() => {
    const list = [...BASE_STEPS];
    if (!hasDiscord(user)) list.push(DISCORD_STEP);
    list.push(SETTINGS_STEP);
    return list;
  }, [user]);

  const step = steps[Math.min(index, steps.length - 1)];

  useEffect(() => {
    if (!active || hasDiscord(user)) return undefined;
    if (typeof api.getOnboardingConfig !== 'function') return undefined;
    let cancelled = false;
    Promise.resolve(api.getOnboardingConfig())
      .then((res) => {
        if (!cancelled) setInviteUrl(res?.data?.discordServerInviteUrl || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [active, user]);

  useEffect(() => {
    if (!active || !step?.route) return;
    if (location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [active, step, location.pathname, navigate]);

  const persist = useCallback(async (status) => {
    mergeUser({ onboarding_tour_status: status });
    try {
      if (typeof api.updateOnboardingTour === 'function') {
        await api.updateOnboardingTour(status);
      }
    } catch (err) {
      console.error('Failed to update tour status', err);
    }
  }, [mergeUser]);

  const handleSkip = () => persist('skipped');
  const handleDefer = () => persist('deferred');
  const handleNext = () => {
    if (index >= steps.length - 1) {
      persist('completed');
      return;
    }
    setIndex((prev) => prev + 1);
  };
  const handleBack = () => setIndex((prev) => Math.max(0, prev - 1));

  if (!active || !step) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-labelledby="onboarding-tour-title">
      <div className={styles.card}>
        <p className={styles.progress}>Step {index + 1} of {steps.length}</p>
        <h2 id="onboarding-tour-title" className={styles.title}>{step.title}</h2>
        <p className={styles.body}>{step.body}</p>
        {step.id === 'discord' && (
          <div className={styles.links}>
            <a href="https://discord.com/download" target="_blank" rel="noreferrer">Download Discord</a>
            <a href="https://discord.com/register" target="_blank" rel="noreferrer">Create a Discord account</a>
            {inviteUrl && (
              <a href={inviteUrl} target="_blank" rel="noreferrer">Join the LOTW server</a>
            )}
          </div>
        )}
        <div className={styles.actions}>
          <button type="button" className={styles.secondary} onClick={handleSkip}>Skip</button>
          <button type="button" className={styles.secondary} onClick={handleDefer}>Show on next login</button>
          <div className={styles.navButtons}>
            <button type="button" className={styles.secondary} onClick={handleBack} disabled={index === 0}>Back</button>
            <button type="button" className={styles.primary} onClick={handleNext}>
              {index >= steps.length - 1 ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
